import { createSelector } from '@reduxjs/toolkit';
import { getOrganization } from '../organization/getOrganization';
import { currentUserUuid } from '../user/currentUserUuid';
import { assetsNotDeleted } from '../assets/assets-not-deleted';
import { constitutionalDocumentsNotDeleted } from '../documents/constitutional-documents-not-deleted';
import { contractsNotDeletedWithParties } from '../documents/contracts-not-deleted-with-parties';
import { documentsNotDeleted } from '../documents/documents-not-deleted';
import { employeesNotDeleted } from '../employees/employees-not-deleted';
import { functionsNotDeleted } from '../responsibilities/functions-not-deleted';
import { tasksNotDeleted } from '../responsibilities/tasks-not-deleted';
import { guidelinesNotDeleted } from '../documents/guidelines-not-deleted';
import { issuesNotDeleted } from '../issues/issues-not-deleted';
import { partnersNotDeleted } from '../partners/partners-not-deleted';
import { contactsNotDeleted } from '../partners/contacts-not-deleted';
import { reportsNotDeleted } from '../documents/reports-not-deleted';
import { riskAssessmentsReducedByName } from '../risk-assessments/risk-assessments-reduced-by-name';
import { substancesNotDeleted } from '../assets/substances-not-deleted';
import { eventOccurrencesNotDeleted } from '../events/event-occurrences-not-deleted';
import { meetingOccurrencesNotDeleted } from '../meetings/meeting-occurrences-not-deleted';
import type { DocForLinking } from './docForLinkingType';
import { getDocForLinking } from './getDocForLinking';
import { sortBy } from 'lodash';
import { pageIds } from '../pages/pageIds';
import { getPages } from '../pages/getPages';
import { storedCollections } from 'src/store/selectors/collections';
import { timeDescription } from '../../utilities';
import { LocalDate } from '../../../utilities/local-date';
import { capitalize } from '../../../utilities/text';

export const docsForLinking = createSelector(
  getOrganization,
  currentUserUuid,
  getPages,
  assetsNotDeleted,
  constitutionalDocumentsNotDeleted,
  contractsNotDeletedWithParties,
  documentsNotDeleted,
  employeesNotDeleted,
  functionsNotDeleted,
  tasksNotDeleted,
  guidelinesNotDeleted,
  issuesNotDeleted,
  partnersNotDeleted,
  contactsNotDeleted,
  reportsNotDeleted,
  riskAssessmentsReducedByName,
  substancesNotDeleted,
  eventOccurrencesNotDeleted,
  meetingOccurrencesNotDeleted,
  storedCollections,
  (
    organization,
    userId,
    pages,
    assets,
    constitutionalDocuments,
    contracts,
    documents,
    employees,
    functions,
    tasks,
    guidelines,
    issues,
    partners,
    contacts,
    reports,
    riskAssessments,
    substances,
    eventOccurrences,
    meetingOccurrences,
    collections,
  ) => {
    const result: DocForLinking[] = [];
    if (organization) {
      const organizationId = organization.organizationId;
      pages.forEach((page) => {
        result.push({
          templateId: page.pageId,
          uuid: page.pageId + '',
          docType: 'pages',
          classified: false,
          noAccess: false,
          href: '/account/' + organization.organizationId + '/' + page.pageId,
          name: page.name,
          dateTimeForOrdering: '',
        });
      });
      assets.forEach((item) => {
        const doc = getDocForLinking(item, 'assets', organizationId, pageIds.assets, userId, '');
        doc.name = item.name + ' ' + item.number;
        result.push(doc);
      });
      constitutionalDocuments.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'constitutionalDocuments', organizationId, pageIds.company, userId, ''));
        }
      });
      contracts.forEach((item) => {
        if (item.name) {
          const doc = getDocForLinking(item, 'contracts', organizationId, pageIds.management, userId, '');
          doc.name = item.nameWithParties;
          result.push(doc);
        }
      });
      documents.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'documents', organizationId, pageIds.management, userId, ''));
        }
      });
      employees.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'employees', organizationId, pageIds.employees, userId, ''));
        }
      });
      functions.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'functions', organizationId, pageIds.management, userId, ''));
        }
      });
      tasks.forEach((item) => {
        if (item.name && !item.locked) {
          const parent = organization.functionsById[item.functionUuid];
          const parentHref = 'functions/' + parent.uuid + '/';
          const pageId = parent.pages[0];
          result.push(getDocForLinking(item, 'tasks', organizationId, pageId, userId, parentHref));
        }
      });
      guidelines.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'guidelines', organizationId, pageIds.management, userId, ''));
        }
      });
      issues.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'issues', organizationId, pageIds.issues, userId, ''));
        }
      });
      partners.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'partners', organizationId, pageIds.partners, userId, ''));
        }
      });
      contacts.forEach((item) => {
        if (item.firstName || item.lastName) {
          const parent = organization.partnersById[item.partnerUuid];
          const parentHref = 'partners/' + parent.uuid + '/';
          const doc = getDocForLinking(item, 'contacts', organizationId, pageIds.partners, userId, parentHref);
          doc.name = item.firstName + ' ' + item.lastName + ' (' + parent.name + ')';
          result.push(doc);
        }
      });
      reports.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'reports', organizationId, pageIds.management, userId, ''));
        }
      });
      riskAssessments.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'riskAssessments', organizationId, pageIds.riskAssessments, userId, ''));
        }
      });
      substances.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'substances', organizationId, pageIds.substances, userId, ''));
        }
      });
      const dateDisplays: Record<string, string> = Object.create(null);
      const timeSuffixes: Record<string, string> = Object.create(null);
      for (const item of eventOccurrences) {
        if (!item.name) continue;
        const date = item.date;
        if (date && dateDisplays[date] === undefined) {
          dateDisplays[date] = LocalDate.fromString(date).toStringForDisplayWithDayOfWeekAndYear();
        }
        const time = item.time;
        if (time && time !== 'NONE') {
          const key = time + '|' + (item.durationMinutes ?? 0);
          if (timeSuffixes[key] === undefined) {
            timeSuffixes[key] = ' ' + timeDescription(time, item.durationMinutes ?? 0, ' kl. ');
          }
        }
      }
      for (const item of meetingOccurrences) {
        if (!item.name) continue;
        const date = item.date;
        if (date && dateDisplays[date] === undefined) {
          dateDisplays[date] = LocalDate.fromString(date).toStringForDisplayWithDayOfWeekAndYear();
        }
        const time = item.time;
        if (time && time !== 'NONE') {
          const key = time + '|' + (item.durationMinutes ?? 0);
          if (timeSuffixes[key] === undefined) {
            timeSuffixes[key] = ' ' + timeDescription(time, item.durationMinutes ?? 0, ' kl. ');
          }
        }
      }
      const buildEventDoc = (
        item: {
          name?: string;
          date?: string;
          time?: string;
          durationMinutes?: number;
          uuid: string;
        },
        type: 'eventOccurrences' | 'meetingOccurrences',
      ): void => {
        if (!item.name) return;
        const doc = getDocForLinking(item, type, organizationId, pageIds.events, userId, '');
        const date = item.date ?? '';
        const time = item.time ?? '';
        let nameSuffix: string;
        if (!date) {
          nameSuffix = 'Snarest';
        } else {
          const dateDisplay = dateDisplays[date];
          const suffix = time && time !== 'NONE' ? timeSuffixes[time + '|' + (item.durationMinutes ?? 0)] : '';
          nameSuffix = capitalize(dateDisplay + suffix);
        }
        doc.name = item.name + ' ' + nameSuffix;
        doc.dateTimeForOrdering = date + ' ' + (time && time !== 'NONE' ? time : '00:00');
        result.push(doc);
      };
      for (const item of eventOccurrences) buildEventDoc(item, 'eventOccurrences');
      for (const item of meetingOccurrences) buildEventDoc(item, 'meetingOccurrences');
      collections.forEach((item) => {
        if (item.name) {
          result.push(getDocForLinking(item, 'collections', organizationId, pageIds.collections, userId, ''));
        }
      });
    }
    return sortBy(result, [(e) => e.dateTimeForOrdering, (e) => e.name]);
  },
);
